import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';

import StaffStatisticsFilters from './components/StaffStatisticsFilters';
import StaffStatisticsList from './components/StaffStatisticsList';
import { IFilters } from './types';
import { downloadStaffStatistics, fetchStaffStatistics } from '../../api/staff-statistics';
import ContentWrapper from '../../components/ContentWrapper';
import ProgressSection from '../../components/ProgressSection';
import { IStaffStatistics } from '../../types/staff-statistics.type';
import { HttpUtils } from '../../utils/http.utils';

const StaffStatistics = React.memo(() => {
  const [statistics, setStatistics] = useState<IStaffStatistics[]>([]);
  const [isSubmitProcess, setSubmitProcess] = useState<boolean>(false);
  const [isExportProcess, setExportProcess] = useState<boolean>(false);
  const [isReady, setReady] = useState<boolean>(false);

  const { t } = useTranslation();

  const handleSubmit = React.useCallback(
    (filters: IFilters) => {
      setSubmitProcess(true);

      if (!isReady) {
        setReady(true);
      }

      fetchStaffStatistics(filters)
        .then((response) => {
          setStatistics(response);
        })
        .catch(() => setStatistics([]))
        .finally(() => setSubmitProcess(false));
    },
    [isReady],
  );

  const handleExport = React.useCallback(
    (filters: IFilters) => {
      setExportProcess(true);

      if (!isReady) {
        setReady(true);
      }

      downloadStaffStatistics(filters)
        .then((response) => HttpUtils.handleBlob(response, 'staff_statistics.xlsx'))
        .finally(() => setExportProcess(false));
    },
    [isReady],
  );

  const renderList = React.useMemo(() => {
    if (!isReady) {
      return null;
    }

    if (isSubmitProcess) {
      return <ProgressSection mt={3} />;
    }

    return <StaffStatisticsList statistics={statistics} />;
  }, [isReady, isSubmitProcess, statistics]);

  return (
    <ContentWrapper title={t('staff_statistics.title')}>
      <StaffStatisticsFilters
        onSubmit={handleSubmit}
        onExport={handleExport}
        isSubmitProcess={isSubmitProcess}
        isExportProcess={isExportProcess}
      />

      {renderList}
    </ContentWrapper>
  );
});

export default StaffStatistics;
